import type { Command, CommandResult, GameState } from "@bd/sim";
import type { FromWorker, ToWorker } from "./protocol";

type Listener = (m: FromWorker) => void;

/** Talks to the rules engine running in a Web Worker. */
export class SimHost {
  private worker: Worker;
  private nextId = 1;
  private waiting = new Map<number, (r: CommandResult) => void>();
  private listeners = new Set<Listener>();

  constructor() {
    this.worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (ev: MessageEvent<FromWorker>) => {
      const m = ev.data;
      if (m.type === "result") {
        this.waiting.get(m.id)?.(m.result);
        this.waiting.delete(m.id);
      }
      for (const l of this.listeners) l(m);
    };
  }

  on(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  send(m: ToWorker): void {
    this.worker.postMessage(m);
  }

  newGame(seed: string, company: string, player = "you", plus?: { notebook: Record<string, number> }): void {
    this.send({ type: "new", seed, company, player, newGamePlus: !!plus, notebook: plus?.notebook });
  }

  load(state: GameState, opts: { player?: string; online?: boolean } = {}): void {
    this.send({ type: "load", state, ...opts });
  }

  command(cmd: Command): Promise<CommandResult> {
    const id = this.nextId++;
    return new Promise((res) => {
      this.waiting.set(id, res);
      this.send({ type: "cmd", id, cmd });
    });
  }

  setSpeed(speed: number): void {
    this.send({ type: "speed", speed });
  }

  step(weeks = 1): void {
    this.send({ type: "step", weeks });
  }
}
